// The volunteer portal channel (ADR 0024): myBRE (bre-loyalty's customer
// app) shows a volunteer their shifts, open shifts, time off and hours, and
// lets them sign up, drop and check themselves in, by calling Hub server to
// server. Volunteers never sign in to Hub and never see Hub.
//
// Who is asking. myBRE has already signed the volunteer in through BRE Auth;
// it asserts their BRE Auth subject on each request and signs the request
// with HUB_VOLUNTEER_SECRET (a secret shared only by Hub's web Worker and
// myBRE). Hub checks the signature, the time, that the nonce is new, and the
// rate per subject, then acts as that subject's Hub person, found by the
// exact (issuer, subject) pair and nothing else. Email, phone and name never
// identify anyone here.
//
// Signature (version 1):
//
//   canonical = [
//     'bre-hub-volunteer-v1',
//     METHOD,                    // upper case, e.g. GET
//     pathWithQuery,             // as Hub receives it, e.g. /internal/volunteer/v1/open-shifts
//     sub,                       // the BRE Auth subject
//     timestamp,                 // unix seconds, as a decimal string
//     nonce,                     // 16-64 chars of [A-Za-z0-9_-], new for every request
//     sha256Hex(body),           // of the exact body bytes; of '' when there is none
//   ].join('\n')
//   signature = base64url(HMAC-SHA256(HUB_VOLUNTEER_SECRET, canonical))   // no padding
//
// Headers: x-hub-volunteer-sub, x-hub-volunteer-timestamp,
// x-hub-volunteer-nonce, x-hub-volunteer-signature. A request more than
// VOLUNTEER_SIGNATURE_MAX_SKEW_SECONDS from Hub's clock, or with a nonce Hub
// has already seen, is refused. Responses are JSON; errors are
// { error: { code, message, warnings? } } with the message written for the
// volunteer.

import { z } from 'zod'
import { UnavailabilityBody } from './shifts'

export const VOLUNTEER_PORTAL_PREFIX = '/internal/volunteer/v1'
export const VOLUNTEER_SIGNATURE_VERSION = 'bre-hub-volunteer-v1'
export const VOLUNTEER_SIGNATURE_MAX_SKEW_SECONDS = 60
// Nonces are kept a little longer than the window either side.
export const VOLUNTEER_NONCE_TTL_SECONDS = 180
// Per subject, per minute: every request, and the ones that change something.
export const VOLUNTEER_RATE_LIMIT_PER_MINUTE = 60
export const VOLUNTEER_WRITE_LIMIT_PER_MINUTE = 12

export const VOLUNTEER_HEADERS = {
  sub: 'x-hub-volunteer-sub',
  timestamp: 'x-hub-volunteer-timestamp',
  nonce: 'x-hub-volunteer-nonce',
  signature: 'x-hub-volunteer-signature',
} as const

// The actor the web Worker signs for core on this channel: a subject with
// this prefix and no person id. Core lets such an actor reach only
// /v1/volunteer-portal/* and resolves the person itself.
export const VOLUNTEER_PORTAL_SUB_PREFIX = 'volunteer-portal:'
export const volunteerPortalSub = (breSub: string) => `${VOLUNTEER_PORTAL_SUB_PREFIX}${breSub}`
export const VOLUNTEER_PORTAL_CHANNEL = 'volunteer-portal'

const NONCE = /^[A-Za-z0-9_-]{16,64}$/
const SUB = /^[A-Za-z0-9_.:@|-]{1,200}$/

const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

export async function sha256Hex(body: string | Uint8Array): Promise<string> {
  const bytes = typeof body === 'string' ? encoder.encode(body) : body
  const digest = await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function volunteerCanonical(input: { method: string; pathWithQuery: string; sub: string; timestamp: string; nonce: string; bodySha256: string }): string {
  return [VOLUNTEER_SIGNATURE_VERSION, input.method.toUpperCase(), input.pathWithQuery, input.sub, input.timestamp, input.nonce, input.bodySha256].join('\n')
}

async function hmac(secret: string, message: string): Promise<string> {
  if (!secret || secret.length < 32) throw new Error('HUB_VOLUNTEER_SECRET must be at least 32 characters')
  const key = await crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  return base64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(message))))
}

// What myBRE does (its own copy lives in bre-loyalty; the test vector in
// volunteer-portal.test.ts keeps the two honest).
export async function signVolunteerRequest(input: { secret: string; method: string; pathWithQuery: string; sub: string; body?: string; now?: number; nonce?: string }): Promise<Record<string, string>> {
  const timestamp = String(Math.floor((input.now ?? Date.now()) / 1000))
  const nonce = input.nonce ?? base64url(crypto.getRandomValues(new Uint8Array(18)))
  const signature = await hmac(input.secret, volunteerCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, sub: input.sub, timestamp, nonce, bodySha256: await sha256Hex(input.body ?? '') }))
  return { [VOLUNTEER_HEADERS.sub]: input.sub, [VOLUNTEER_HEADERS.timestamp]: timestamp, [VOLUNTEER_HEADERS.nonce]: nonce, [VOLUNTEER_HEADERS.signature]: signature }
}

function equalStrings(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

export type VolunteerSignatureFailure = 'missing' | 'malformed' | 'stale' | 'bad_signature'

// Checks everything but the nonce's novelty (the caller keeps the nonces).
export async function verifyVolunteerRequest(input: {
  secret: string
  method: string
  pathWithQuery: string
  headers: { get(name: string): string | null }
  body: string
  now?: number
}): Promise<{ ok: true; sub: string; nonce: string; timestamp: number } | { ok: false; reason: VolunteerSignatureFailure }> {
  const sub = input.headers.get(VOLUNTEER_HEADERS.sub)
  const ts = input.headers.get(VOLUNTEER_HEADERS.timestamp)
  const nonce = input.headers.get(VOLUNTEER_HEADERS.nonce)
  const signature = input.headers.get(VOLUNTEER_HEADERS.signature)
  if (!sub || !ts || !nonce || !signature) return { ok: false, reason: 'missing' }
  if (!SUB.test(sub) || !/^\d{9,11}$/.test(ts) || !NONCE.test(nonce) || !/^[A-Za-z0-9_-]{43}$/.test(signature)) return { ok: false, reason: 'malformed' }
  const timestamp = Number(ts)
  const now = Math.floor((input.now ?? Date.now()) / 1000)
  if (Math.abs(now - timestamp) > VOLUNTEER_SIGNATURE_MAX_SKEW_SECONDS) return { ok: false, reason: 'stale' }
  const expected = await hmac(input.secret, volunteerCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, sub, timestamp: ts, nonce, bodySha256: await sha256Hex(input.body) }))
  if (!equalStrings(expected, signature)) return { ok: false, reason: 'bad_signature' }
  return { ok: true, sub, nonce, timestamp }
}

// ------------------------------------------------------------------ shapes
//
// Only what a volunteer may see about their own shifts and open ones: never
// another person's name or contact, an emergency contact, a shift's
// internal notes, who leads it, warnings or override reasons, or anything
// about pay.

export type VolunteerStatus = 'active' | 'applicant' | 'inactive' | 'alumni' | 'none'

export type VolunteerMe = {
  // Whether this subject may use the volunteer pages: a Hub person with an
  // active volunteer workforce record (ADR 0024).
  isVolunteer: boolean
  status: VolunteerStatus
  // Set when isVolunteer is false: what to tell them, in their words.
  notSetUpReason: string | null
  application: { status: 'new' | 'accepted' | 'declined' | 'waitlisted'; submittedAt: string } | null
  settings: { dropCutoffHours: number; checkinWindowMinutes: number }
  nowLocal: string
  // ADR 0061: may use the Qualifications page (an active or applicant staff
  // or volunteer record of any type, volunteer or not).
  qualifications: boolean
}

// eventId and eventStartsOn group a volunteer's shifts under their event.
export type VolunteerShiftPlace = { eventId: string | null; eventName: string | null; eventStartsOn: string | null; facilityName: string | null; spaceName: string | null; departmentName: string | null }

export type VolunteerShift = VolunteerShiftPlace & {
  shiftId: string
  assignmentId: string | null
  role: string
  kind: 'volunteer' | 'staff'
  startsLocal: string
  endsLocal: string
  status: 'published' | 'canceled'
  attendance: 'scheduled' | 'checked_in' | 'checked_out' | 'no_show' | null
  checkedInAt: string | null
  checkedOutAt: string | null
  attendedMinutes: number | null
  canCheckIn: boolean
  canCheckOut: boolean
  canDrop: boolean
  // When dropping stops being possible (venue-local), for volunteer shifts.
  dropCutoffLocal: string | null
  // Why they can't drop, in their words, when canDrop is false and they're on it.
  dropBlockedReason: string | null
  requiredQualifications: string[]
  requiredTraining: string[]
}

export type VolunteerOpenShift = VolunteerShift & {
  spotsLeft: number
  onShift: boolean
  canSignUp: boolean
  // Every reason they can't sign up, worded for them (ADR 0017's rules).
  blockers: { code: string; message: string }[]
}

export type VolunteerShiftsResponse = { upcoming: VolunteerShift[]; past: VolunteerShift[]; nowLocal: string; settings: VolunteerMe['settings'] }
export type VolunteerOpenShiftsResponse = { shifts: VolunteerOpenShift[]; nowLocal: string; settings: VolunteerMe['settings'] }
export type VolunteerTimeOff = { id: string; startsLocal: string; endsLocal: string; note: string | null }
export type VolunteerHoursResponse = {
  year: number
  approvedMinutes: number
  pendingMinutes: number
  approvedHours: number
  pendingHours: number
  recent: { workedOn: string; minutes: number; hours: number; status: 'pending' | 'approved' | 'rejected'; label: string }[]
}

// The same rules as a staff member recording their own time off in Hub.
export const VolunteerTimeOffBody = UnavailabilityBody

// "Volunteer with BRE": what myBRE knows about the person, as evidence for
// the coordinator. It never identifies anyone; the subject does.
export const VolunteerInterestBody = z.object({
  displayName: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
})

// ------------------------------------------------------- qualifications
//
// ADR 0061: a staff member or volunteer sees their own qualifications and
// reports one (with evidence files) for staff to verify. Open to anyone
// with an active or applicant staff or volunteer record, not only
// volunteers (VolunteerMe.qualifications). Background checks never appear
// here and are never self-reported. Nothing here names anyone else: staff
// are "BRE staff".

export type VolunteerEvidence = { id: string; filename: string | null; contentType: string | null; sizeBytes: number | null; addedAt: string; addedBy: 'you' | 'staff' }

export type VolunteerQualificationType = {
  id: string
  name: string
  description: string | null
  validityMonths: number | null
  evidenceRequired: boolean
  asksNumber: boolean
}

export type VolunteerQualification = {
  id: string
  typeId: string
  typeName: string
  issuedOn: string | null
  expiresOn: string | null
  state: 'current' | 'expiring' | 'expired' | 'no_expiry'
  credentialNumber: string | null
  evidence: VolunteerEvidence[]
}

export type VolunteerSubmission = {
  id: string
  typeId: string
  typeName: string
  issuedOn: string | null
  expiresOn: string | null
  credentialNumber: string | null
  note: string | null
  status: 'pending' | 'verified' | 'rejected' | 'withdrawn'
  submittedAt: string
  decidedAt: string | null
  // Written by staff for the person, when it wasn't accepted.
  rejectReason: string | null
  evidence: VolunteerEvidence[]
}

export type VolunteerQualificationsResponse = {
  // Types they may report themselves (that apply to them, not retired).
  types: VolunteerQualificationType[]
  // Verified, current (not retired), the latest of each type.
  qualifications: VolunteerQualification[]
  // Waiting, and decided in the last 180 days.
  submissions: VolunteerSubmission[]
  limits: { maxFiles: number; maxBytes: number; contentTypes: readonly string[] }
  today: string
}

// What myBRE sends. Files travel base64 in the JSON; Hub's web Worker
// checks each one's size and first bytes, stores it, and passes core only
// the stored file.
const PortalFile = z.object({ filename: z.string().trim().max(255).nullable().optional(), contentType: z.string().max(100), base64: z.string().min(4) })
export type VolunteerPortalFile = z.infer<typeof PortalFile>
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
export const VolunteerQualificationSubmitBody = z
  .object({
    typeId: z.string().min(1).max(100),
    issuedOn: DAY.nullable().optional(),
    expiresOn: DAY.nullable().optional(),
    credentialNumber: z.string().trim().max(80).nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    files: z.array(PortalFile).max(3).default([]),
  })
  .refine((b) => !b.issuedOn || !b.expiresOn || b.expiresOn >= b.issuedOn, { message: 'The expiry date must be on or after the issue date', path: ['expiresOn'] })
export const VolunteerEvidenceBody = z.object({ files: z.array(PortalFile).min(1).max(3) })
// The total body allowed on the two upload routes: three files of 10 MB,
// base64, and the rest of the JSON.
export const VOLUNTEER_UPLOAD_MAX_BODY = Math.ceil(3 * 10 * 1024 * 1024 * 1.34) + 8_000
